import { useState } from 'react';

const didCache = new Map();

async function resolveDid(actor) {
  if (actor.startsWith('did:')) return actor;
  if (didCache.has(actor)) return didCache.get(actor);
  const res = await fetch(
    `https://api.bsky.app/xrpc/com.atproto.identity.resolveHandle?handle=${encodeURIComponent(actor)}`
  );
  const data = await res.json();
  if (!data.did) throw new Error(`Could not resolve handle: ${actor}`);
  didCache.set(actor, data.did);
  return data.did;
}

export async function resolveFeedUri({ user, feed }) {
  if (!feed) return null;
  if (feed.startsWith('at://')) return feed;
  const did = await resolveDid(user || 'pics.yernemm.xyz');
  return `at://${did}/app.bsky.feed.generator/${feed}`;
}

async function buildUrl(cursor, source) {
  const feedUri = await resolveFeedUri(source);
  const c = encodeURIComponent(cursor);
  if (feedUri) {
    return `https://api.bsky.app/xrpc/app.bsky.feed.getFeed?feed=${encodeURIComponent(feedUri)}&limit=20&cursor=${c}`;
  }
  const actor = source.user || 'pics.yernemm.xyz';
  return `https://api.bsky.app/xrpc/app.bsky.feed.getAuthorFeed?actor=${encodeURIComponent(actor)}&filter=posts_with_media&limit=20&cursor=${c}`;
}

async function LoadItems(cursor, source) {
  const url = await buildUrl(cursor, source);
  const res = await fetch(url);
  const resData = await res.json();

  if (resData.error) {
    throw new Error(`${resData.error}: ${resData.message || 'Unknown error'}`);
  }

  const newImages = [];
  if (resData.feed) {
    resData.feed.forEach(({ post }) => {
      if (!post.embed?.images) return;
      const authorHandle = post.author?.handle ?? source.user;
      post.embed.images.forEach(postedImage => {
        newImages.push({
          src: postedImage.fullsize,
          thumb: postedImage.thumb,
          text: post.record.text,
          postUri: post.uri,
          postUrl: `https://bsky.app/profile/${authorHandle}/post/${post.uri.split('/').pop()}`,
        });
      });
    });
  }

  return {
    hasNextPage: Boolean(resData.cursor),
    data: newImages,
    cursor: resData.cursor,
  };
}

async function loadWithRetry(cursor, source) {
  try {
    return await LoadItems(cursor, source);
  } catch (err) {
    await new Promise(r => setTimeout(r, 1500));
    return await LoadItems(cursor, source);
  }
}

export function useLoadItems(source) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [error, setError] = useState();
  const [cursor, setCursor] = useState('');

  async function loadMore() {
    setLoading(true);
    try {
      const { data, hasNextPage: newHasNextPage, cursor: newCursor } = await loadWithRetry(cursor, source);
      setItems(current => [...current, ...data]);
      setHasNextPage(newHasNextPage);
      setCursor(newCursor);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Something went wrong'));
    } finally {
      setLoading(false);
    }
  }

  return { loading, items, hasNextPage, error, loadMore };
}
